import { createElement, isValidElement, type ComponentType, type ReactNode } from 'react';

import RejectAnimation from '@/components/animations/RejectAnimation';
import { cn } from '@/lib/utils';

import { Heading, Text } from './Typography';

type EmptyStateIcon = ComponentType<{ className?: string }> | ReactNode;

interface EmptyStateProps {
    icon?: EmptyStateIcon;
    title: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
    className?: string;
    visual?: 'animation' | 'icon' | 'none';
    showRejectAnimation?: boolean;
}

function renderIcon(icon?: EmptyStateIcon) {
    if (!icon) return null;
    if (isValidElement(icon)) return icon;

    return createElement(icon as ComponentType<{ className?: string }>, {
        className: 'h-7 w-7',
    });
}

export default function EmptyState({
    icon,
    title,
    description,
    action,
    className,
    visual,
    showRejectAnimation = true,
}: EmptyStateProps) {
    const iconNode = renderIcon(icon);
    const resolvedVisual = visual ?? (showRejectAnimation ? 'animation' : 'icon');

    return (
        <div
            className={cn(
                'rounded-[calc(var(--radius)+0.25rem)] border border-dashed border-border-strong/80 bg-surface/95 px-6 py-12 text-center shadow-elevation-1',
                className,
            )}
        >
            <div className="mx-auto flex max-w-md flex-col items-center gap-4">
                {resolvedVisual === 'animation' ? (
                    <div className="h-16 w-16">
                        <RejectAnimation />
                    </div>
                ) : resolvedVisual === 'icon' ? (
                    iconNode && (
                        <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-brand/15 bg-brand-subtle text-brand shadow-sm">
                            {iconNode}
                        </div>
                    )
                ) : null}
                <div className="space-y-2">
                    <Heading variant="empty">{title}</Heading>
                    {description && <Text variant="description">{description}</Text>}
                </div>
                {action}
            </div>
        </div>
    );
}
